import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Link } from "@inertiajs/react";
import type { ReactNode } from "react";
import type { IndexPagination as IndexPaginationData } from "@/types";
import { paginationPages } from "@/utils/pagination";
import { scrollMainToId } from "@/utils/scrollMain";

type Props = {
  pagination: IndexPaginationData;
  itemLabel?: string;
  buildHref: (page: number) => string;
  id?: string;
  only?: string[];
  preserveScroll?: boolean;
  preserveState?: boolean;
  hideWhenSinglePage?: boolean;
  busy?: boolean;
  scrollToId?: string;
  className?: string;
  onStart?: () => void;
  onFinish?: () => void;
};

export default function IndexPagination({
  pagination,
  itemLabel,
  buildHref,
  id,
  only,
  preserveScroll = false,
  preserveState = false,
  hideWhenSinglePage = false,
  busy = false,
  scrollToId,
  className = "",
  onStart,
  onFinish,
}: Props) {
  if (hideWhenSinglePage && pagination.total_pages <= 1) return null;

  const handleStart = () => {
    if (scrollToId && window.matchMedia("(max-width: 767px)").matches) {
      scrollMainToId(scrollToId);
    }

    onStart?.();
  };

  return (
    <div
      id={id}
      className={`flex ${itemLabel ? "flex-col items-center justify-between gap-4 sm:flex-row" : "w-full justify-center md:w-auto"} ${className} ${busy ? "pointer-events-none opacity-50" : ""}`}
      aria-busy={busy}
    >
      {itemLabel ? (
        <div className="order-2 text-sm text-gray-500 sm:order-1">
          Showing{" "}
          <span className="font-medium">
            {pagination.range_start} to {pagination.range_end}
          </span>{" "}
          of <span className="font-medium">{pagination.total_count}</span> {itemLabel}
        </div>
      ) : null}

      <div className={`flex items-center gap-0.5 ${itemLabel ? "order-1 sm:order-2" : ""}`}>
        <PaginationLink
          href={buildHref(pagination.current_page - 1)}
          disabled={pagination.current_page === 1}
          label="Previous"
          only={only}
          preserveScroll={preserveScroll}
          preserveState={preserveState}
          onStart={handleStart}
          onFinish={onFinish}
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </PaginationLink>

        {paginationPages(pagination).map((page) => (
          <PaginationLink
            key={page}
            href={buildHref(page)}
            disabled={page === pagination.current_page}
            active={page === pagination.current_page}
            only={only}
            preserveScroll={preserveScroll}
            preserveState={preserveState}
            onStart={handleStart}
            onFinish={onFinish}
          >
            {page}
          </PaginationLink>
        ))}

        <PaginationLink
          href={buildHref(pagination.current_page + 1)}
          disabled={pagination.current_page >= pagination.total_pages}
          label="Next"
          only={only}
          preserveScroll={preserveScroll}
          preserveState={preserveState}
          onStart={handleStart}
          onFinish={onFinish}
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </PaginationLink>
      </div>
    </div>
  );
}

function PaginationLink({
  href,
  disabled,
  active = false,
  label,
  only,
  preserveScroll,
  preserveState,
  onStart,
  onFinish,
  children,
}: {
  href: string;
  disabled: boolean;
  active?: boolean;
  label?: string;
  only?: string[];
  preserveScroll: boolean;
  preserveState: boolean;
  onStart?: () => void;
  onFinish?: () => void;
  children: ReactNode;
}) {
  const className = [
    "inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-sm tabular-nums",
    active ? "font-medium text-primary" : "text-gray-500",
    !disabled &&
      "hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1",
    disabled && !active && "cursor-not-allowed opacity-40",
  ]
    .filter(Boolean)
    .join(" ");

  if (disabled) {
    if (label === "Previous" || label === "Next") {
      return (
        <button type="button" className={className} disabled aria-label={label}>
          {children}
        </button>
      );
    }

    return (
      <span className={className} aria-current={active ? "page" : undefined} aria-disabled="true">
        {children}
        {label ? <span className="sr-only">{label}</span> : null}
      </span>
    );
  }

  return (
    <Link
      href={href}
      className={className}
      aria-label={label}
      only={only}
      preserveScroll={preserveScroll}
      preserveState={preserveState}
      onStart={onStart}
      onFinish={onFinish}
    >
      {children}
      {label ? <span className="sr-only">{label}</span> : null}
    </Link>
  );
}
